// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Photo Gallery
// Browse, filter, and view all debris photos by zone, type, date
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  SimpleGrid, TextInput, Modal,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconRefresh, IconTrash, IconPhoto, IconMapPin,
  IconDownload,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { WORKER_URL } from '../../lib/api-switch';

const PARENT_TYPES = [
  { value: 'all', label: 'All Sources' },
  { value: 'field_pickup', label: 'Field Pickups' },
  { value: 'load_ticket', label: 'Load Tickets' },
  { value: 'monitor', label: 'Monitor Station' },
  { value: 'zone', label: 'Zones' },
  { value: 'site', label: 'Disposal Sites' },
];

// Proxy Ignatius file URLs through the worker for auth
function proxyUrl(url: string): string {
  if (!url) return '';
  if (url.includes('api.ignatius.io')) {
    const path = url.replace(/^https?:\/\/api\.ignatius\.io/, '');
    return `${WORKER_URL}/api/ignatius${path}`;
  }
  return url;
}

export default function Photos() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { photos, loadingPhotos, loadPhotos, deletePhoto, loadTickets, loadLoadTickets } = useDebrisOpsStore();
  const { zones, loadZones } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [zoneFilter, setZoneFilter] = useState<string | null>(null);
  const [viewPhoto, setViewPhoto] = useState<typeof photos[0] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<typeof photos[0] | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadPhotos(); loadZones(); loadLoadTickets(); }, [loadPhotos, loadZones, loadLoadTickets]);

  // Synthesize photo entries from load ticket stops (before/after photos stored as base64 in pickupStopsJson)
  const ticketPhotos = useMemo(() => {
    const iTickets = loadTickets;
    const synth: Array<{ id: number; Rid: number; incidentId: string; parentType: string; parentId: string; photoUrl: string; caption: string; geoLat: string; geoLng: string; takenAt: string; uploadedBy: string }> = [];
    for (const t of iTickets) {
      try {
        const stops: any[] = JSON.parse(t.pickupStopsJson || '[]');
        stops.forEach((s: any, idx: number) => {
          const label = `${t.ticketNumber} Stop ${idx + 1}`;
          const debrisLabel = DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType ?? '';
          if (s.beforePhotoData) {
            const sid = -(t.id * 1000 + idx * 10 + 1);
            synth.push({
              id: sid, Rid: sid,
              incidentId: t.incidentId,
              parentType: 'load_ticket',
              parentId: String(t.id),
              photoUrl: s.beforePhotoData,
              caption: `${label} — Before${debrisLabel ? ` (${debrisLabel})` : ''}`,
              geoLat: s.lat || '',
              geoLng: s.lng || '',
              takenAt: s.timestamp || t.pickupTime || '',
              uploadedBy: '',
            });
          }
          if (s.afterPhotoData) {
            const sid = -(t.id * 1000 + idx * 10 + 2);
            synth.push({
              id: sid, Rid: sid,
              incidentId: t.incidentId,
              parentType: 'load_ticket',
              parentId: String(t.id),
              photoUrl: s.afterPhotoData,
              caption: `${label} — After${debrisLabel ? ` (${debrisLabel})` : ''}`,
              geoLat: s.lat || '',
              geoLng: s.lng || '',
              takenAt: s.timestamp || t.pickupTime || '',
              uploadedBy: '',
            });
          }
        });
      } catch { /* skip malformed */ }
    }
    return synth;
  }, [loadTickets, incidentId]);

  const iPhotos = useMemo(() => {
    const dbPhotos = incidentId ? photos : [];
    return [...dbPhotos, ...ticketPhotos];
  }, [photos, ticketPhotos, incidentId]);
  const iZones = incidentId ? zones : [];

  const zoneSelect = useMemo(() => [
    { value: 'all', label: 'All Zones' },
    ...iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `Zone #${z.id}` })),
  ], [iZones]);

  const filtered = useMemo(() => {
    let list = iPhotos;
    if (typeFilter && typeFilter !== 'all') list = list.filter(p => p.parentType === typeFilter);
    if (zoneFilter && zoneFilter !== 'all') list = list.filter(p =>
      p.parentId === zoneFilter || (p as unknown as Record<string, unknown>).zoneId === zoneFilter,
    );
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.caption?.toLowerCase().includes(q)) ||
        (p.uploadedBy?.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [iPhotos, typeFilter, zoneFilter, search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePhoto(deleteTarget.id);
      notifications.show({ message: 'Photo deleted', color: '#939598' });
      setDeleteTarget(null);
      if (viewPhoto?.id === deleteTarget.id) setViewPhoto(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setDeleting(false); }
  };

  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? id;

  const parentTypeLabel = (type: string) => {
    const found = PARENT_TYPES.find(t => t.value === type);
    return found?.label ?? type;
  };

  // Stats
  const photosByType = useMemo(() => {
    const counts: Record<string, number> = {};
    iPhotos.forEach(p => {
      const t = p.parentType || 'unknown';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [iPhotos]);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Photo Gallery</Title>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          loading={loadingPhotos} onClick={() => loadPhotos()}>Refresh</Button>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Paper p="sm" radius="md" withBorder>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total Photos</Text>
          <Text size="xl" fw={700}>{iPhotos.length}</Text>
        </Paper>
        {Object.entries(photosByType).slice(0, 3).map(([type, count]) => (
          <Paper key={type} p="sm" radius="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{parentTypeLabel(type)}</Text>
            <Text size="xl" fw={700}>{count}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Filters */}
      <Group gap="sm">
        <TextInput placeholder="Search captions, uploader..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={PARENT_TYPES} value={typeFilter ?? 'all'}
          onChange={v => setTypeFilter(v)} size="xs" w={160} />
        <Select data={zoneSelect} value={zoneFilter ?? 'all'}
          onChange={v => setZoneFilter(v)} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed">{filtered.length} photo{filtered.length !== 1 ? 's' : ''}</Text>

      {/* Photo grid */}
      {filtered.length === 0 ? (
        <Paper p="xl" radius="md" withBorder style={{ textAlign: 'center' }}>
          <IconPhoto size={48} color="#ccc" />
          <Text c="dimmed" mt="sm">{loadingPhotos ? 'Loading photos...' : 'No photos found'}</Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }}>
          {filtered.map(photo => (
            <Paper key={photo.id} radius="md" withBorder style={{ overflow: 'hidden', cursor: 'pointer' }}
              onClick={() => setViewPhoto(photo)}>
              <div style={{ height: 160, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {photo.photoUrl ? (
                  <img src={proxyUrl(photo.photoUrl)} alt={photo.caption || 'Photo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <IconPhoto size={48} color="#ccc" />
                )}
              </div>
              <div style={{ padding: 8 }}>
                <Text size="xs" fw={500} lineClamp={2}>{photo.caption || 'No caption'}</Text>
                <Group gap={4} mt={4}>
                  <Badge size="xs" variant="light">{parentTypeLabel(photo.parentType)}</Badge>
                  {photo.geoLat && photo.geoLng && (
                    <Badge size="xs" variant="light" color="var(--color-success)" leftSection={<IconMapPin size={8} />}>GPS</Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed" mt={2}>{photo.takenAt || ''}</Text>
              </div>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Photo detail modal */}
      <Modal opened={!!viewPhoto} onClose={() => setViewPhoto(null)}
        title="Photo Detail" centered size="lg">
        {viewPhoto && (
          <Stack>
            <div style={{ textAlign: 'center', background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
              {viewPhoto.photoUrl ? (
                <img src={proxyUrl(viewPhoto.photoUrl)} alt={viewPhoto.caption || 'Photo'}
                  style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain' }} />
              ) : (
                <div style={{ padding: 48 }}>
                  <IconPhoto size={64} color="#ccc" />
                  <Text c="dimmed" mt="sm">No image file uploaded</Text>
                </div>
              )}
            </div>

            <Text fw={600}>{viewPhoto.caption || 'No caption'}</Text>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <div>
                <Text size="xs" c="dimmed">Source</Text>
                <Badge>{parentTypeLabel(viewPhoto.parentType)}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Uploaded By</Text>
                <Text size="sm">{viewPhoto.uploadedBy || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Taken At</Text>
                <Text size="sm">{viewPhoto.takenAt || '-'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Location</Text>
                {viewPhoto.geoLat && viewPhoto.geoLng ? (
                  <Text size="sm">{viewPhoto.geoLat}, {viewPhoto.geoLng}</Text>
                ) : (
                  <Text size="sm" c="dimmed">No GPS</Text>
                )}
              </div>
              {viewPhoto.parentId && (
                <div>
                  <Text size="xs" c="dimmed">Zone</Text>
                  <Text size="sm">{zoneName(viewPhoto.parentId)}</Text>
                </div>
              )}
            </SimpleGrid>

            <Group justify="space-between">
              {viewPhoto.photoUrl && (
                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                  component="a" href={proxyUrl(viewPhoto.photoUrl)} target="_blank" download>
                  Download
                </Button>
              )}
              {canEdit && viewPhoto.id > 0 && (
              <Button size="xs" color="var(--color-error)" variant="light" leftSection={<IconTrash size={14} />}
                onClick={() => { setDeleteTarget(viewPhoto); }}>
                Delete
              </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Photo" centered size="sm">
        <Stack>
          <Text size="sm">Delete this photo? This cannot be undone.</Text>
          {deleteTarget?.caption && <Text size="sm" c="dimmed">{deleteTarget.caption}</Text>}
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={deleting} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
